import React from 'react';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system' | 'tool';
  content: string;
  timestamp: string;
  tool_calls?: any[];
  metadata?: Record<string, any>;
}

interface MessageListProps {
  messages: Message[];
  isTyping?: boolean;
  className?: string;
}

const MessageList: React.FC<MessageListProps> = ({
  messages,
  isTyping = false,
  className = ''
}) => {
  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const MessageAvatar: React.FC<{ role: string }> = ({ role }) => {
    const avatarClass = "w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold";

    switch (role) {
      case 'user':
        return (
          <div className={`${avatarClass} bg-blue-500`}>U</div>
        );
      case 'assistant':
        return (
          <div className={`${avatarClass} bg-green-500`}>AI</div>
        );
      case 'system':
        return (
          <div className={`${avatarClass} bg-gray-50 dark:bg-gray-900`}>S</div>
        );
      case 'tool':
        return (
          <div className={`${avatarClass} bg-purple-500`}>T</div>
        );
      default:
        return (
          <div className={`${avatarClass} bg-gray-400`}>?</div>
        );
    }
  };

  const MessageContent: React.FC<{ content: string; role: string }> = ({ content, role }) => {
    const bgClass = role === 'user'
      ? 'bg-blue-50 dark:bg-blue-900/30 text-gray-900 dark:text-warm-white'
      : role === 'assistant'
      ? 'bg-green-50 dark:bg-green-900/20 text-gray-900 dark:text-warm-white'
      : 'bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-warm-white';

    return (
      <div className={`flex-1 p-4 rounded-lg ${bgClass}`}>
        <ReactMarkdown
          components={{
            code({ className, children, ...props }: any) {
              const match = /language-(\w+)/.exec(className || '');
              const isCodeBlock = Boolean(match);
              return isCodeBlock ? (
                <SyntaxHighlighter
                  style={vscDarkPlus as any}
                  language={match![1]}
                  PreTag="div"
                  {...props}
                >
                  {String(children).replace(/\n$/, '')}
                </SyntaxHighlighter>
              ) : (
                <code className="bg-gray-200 dark:bg-gray-700 px-1 py-0.5 rounded text-sm" {...props}>
                  {children}
                </code>
              );
            },
            p: ({ children }: any) => <p className="mb-2 last:mb-0">{children}</p>,
            ul: ({ children }: any) => <ul className="list-disc list-inside mb-2">{children}</ul>,
            ol: ({ children }: any) => <ol className="list-decimal list-inside mb-2">{children}</ol>,
            li: ({ children }: any) => <li className="mb-1">{children}</li>,
            h1: ({ children }: any) => <h1 className="text-xl font-bold mb-2">{children}</h1>,
            h2: ({ children }: any) => <h2 className="text-lg font-bold mb-2">{children}</h2>,
            h3: ({ children }: any) => <h3 className="text-base font-bold mb-2">{children}</h3>,
            blockquote: ({ children }: any) => (
              <blockquote className="border-l-4 border-gray-300 dark:border-gray-600 pl-4 italic my-2">
                {children}
              </blockquote>
            ),
          }}
        >
          {content}
        </ReactMarkdown>
      </div>
    );
  };

  const ToolCallDisplay: React.FC<{ toolCalls: any[] }> = ({ toolCalls }) => {
    return (
      <div className="mt-2 p-2 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
        <div className="text-xs font-semibold text-purple-700 dark:text-purple-300 mb-1">Tool Calls:</div>
        {toolCalls.map((call, index) => (
          <div key={index} className="text-xs text-purple-600 dark:text-purple-400 mb-1">
            <span className="font-medium">{call.function?.name || 'Unknown'}</span>
            {call.function?.arguments && (
              <span className="ml-2 text-gray-600 dark:text-gray-400">
                ({call.function.arguments})
              </span>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {messages.map((message) => (
        <div key={message.id} className="flex gap-3">
          <MessageAvatar role={message.role} />

          <div className="flex-1">
            <div className="flex items-baseline gap-2 mb-1">
              <span className="font-semibold text-sm capitalize text-gray-900 dark:text-warm-white">
                {message.role === 'assistant' ? 'AI Assistant' : message.role}
              </span>
              <span className="text-xs text-gray-500 dark:text-gray-400">
                {formatTimestamp(message.timestamp)}
              </span>
            </div>

            <MessageContent content={message.content} role={message.role} />

            {message.tool_calls && message.tool_calls.length > 0 && (
              <ToolCallDisplay toolCalls={message.tool_calls} />
            )}

            {message.metadata?.error && (
              <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 rounded-lg text-sm text-red-600 dark:text-red-400">
                ⚠️ Error occurred
              </div>
            )}
          </div>
        </div>
      ))}

      {isTyping && (
        <div className="flex gap-3">
          <MessageAvatar role="assistant" />
          <div className="flex-1">
            <div className="flex items-baseline gap-2 mb-1">
              <span className="font-semibold text-sm">AI Assistant</span>
            </div>
            <div className="p-4 rounded-lg bg-green-50 dark:bg-green-900/20 text-gray-600 dark:text-gray-400">
              <div className="flex gap-1">
                <span className="animate-bounce" style={{ animationDelay: '0ms' }}>●</span>
                <span className="animate-bounce" style={{ animationDelay: '150ms' }}>●</span>
                <span className="animate-bounce" style={{ animationDelay: '300ms' }}>●</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageList;